import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { LoadingIcon } from '@/components/icons';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useTreePrompt from '@/data/hook/useTreePrompt';
import { promptInstructionsQueryKeys } from '@/data/services/querykeys';
import PromptInstruction, {
  PromptCategoryEnum,
} from '@/models/PromptInstruction';
import useFilterParams from '@/utils/UseFilterParams';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PromptsFiltersParams } from '../PromptsPageContent';
import PromptsTreeList from './PromptsTreeList';

export default function PromptsTreeRoot() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts',
  });
  const { cacheObserver } = useTreePrompt();
  const { setFilterParams, filterParams } =
    useFilterParams<PromptsFiltersParams>();

  const {
    results: prompts,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    cacheManager,
  } = useInfiniteService(
    promptInstructionsQueryKeys.list({
      isParent: true,
      category: Object.values(PromptCategoryEnum),
    })._ctx.infinity,
  );
  useEffect(() => {
    cacheObserver.subscribe({
      id: 0,
      action: (prompt?: PromptInstruction) => {
        if (!prompt) return;
        cacheManager.update(prompt);
      },
    });
    return () => cacheObserver.unsubscribe(0);
  }, [cacheManager, cacheObserver]);

  useEffect(() => {
    if (filterParams.get('prompt')) return;

    if (prompts.length === 0) return;

    setFilterParams({
      prompt: prompts[0].id,
    });
  }, [prompts, setFilterParams, filterParams]);

  return (
    <div className="relative w-1/5 min-w-[250px] max-w-[500px] min-h-24 p-3 border border-base-content/20 rounded-xl bg-base-100">
      <Text text={t('sidebarTitle')} className="text-base-content mb-2" />

      <ConditionalRenderer condition={isInitialLoading}>
        <div className="w-full h-full flex justify-center items-center">
          <LoadingIcon className="w-5 h-5 text-primary" />
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!isInitialLoading}>
        <PromptsTreeList
          items={prompts}
          isRoot
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </ConditionalRenderer>
    </div>
  );
}
